import { styled } from "@mui/material/styles";
import {
  Container,
  Paper,
  Box,
  TextField,
  Button,
  Chip,
  IconButton,
} from "@mui/material";

export const PRIMARY_COLOR = "#f58220";
export const SECONDARY_COLOR = "#02b7cf";
export const NEUTRAL_50 = "#fafafa";
export const NEUTRAL_100 = "#f5f5f5";
export const NEUTRAL_200 = "#e5e5e5";
export const NEUTRAL_800 = "#262626";
export const NEUTRAL_900 = "#171717";

export const ModernContainer = styled(Container)({
  minHeight: "100vh",

  paddingBottom: "3rem",
  position: "relative",
  paddingTop: "80px",
  overflow: "visible",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "300px",
    borderRadius: "0 0 50px 50px",
    zIndex: -1,
  },
});

export const HeaderCard = styled(Paper)({
  padding: "2rem",
  marginBottom: "2rem",
  background: "rgba(255, 255, 255, 0.95)",
  backdropFilter: "blur(10px)",
  borderRadius: "20px",
  border: `1px solid rgba(245, 130, 32, 0.1)`,
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.06)",
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "4px",
    background: `linear-gradient(90deg, ${PRIMARY_COLOR})`,
  },
});

export const SectionCard = styled(Paper)({
  padding: "1.75rem",
  marginBottom: "1.5rem",
  background: "rgba(255, 255, 255, 0.98)",
  backdropFilter: "blur(20px)",
  borderRadius: "16px",
  border: `1px solid rgba(0, 0, 0, 0.04)`,
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.04)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  position: "relative",
  overflow: "visible",
});

export const SectionHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  marginBottom: "20px",
  paddingBottom: "12px",
  borderBottom: `1px solid ${NEUTRAL_100}`,
});

export const SectionIcon = styled(Box)({
  width: "40px",
  height: "40px",
  borderRadius: "12px",
  background: `${PRIMARY_COLOR}15`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: PRIMARY_COLOR,
  fontSize: "20px",
});

export const StyledIconButton = styled(IconButton)({
  background: `${PRIMARY_COLOR}10`,
  "&:hover": {
    background: `${PRIMARY_COLOR}20`,
  },
});

export const StyledTextField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: NEUTRAL_50,
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: "#fff",
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: `${PRIMARY_COLOR}40`,
      },
    },
    "&.Mui-focused": {
      backgroundColor: "#fff",
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: PRIMARY_COLOR,
        borderWidth: "2px",
      },
    },
  },
  "& .MuiInputLabel-root": {
    fontWeight: 500,
    "&.Mui-focused": {
      color: PRIMARY_COLOR,
    },
  },
});

export const ImageUploadContainer = styled(Box)({
  border: `2px dashed ${PRIMARY_COLOR}40`,
  borderRadius: "16px",
  padding: "2rem",
  minHeight: "280px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
  gap: "16px",
  position: "relative",
  background: `${PRIMARY_COLOR}04`,
  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  cursor: "pointer",
  "&:hover": {
    borderColor: `${PRIMARY_COLOR}60`,
    background: `${PRIMARY_COLOR}08`,
    transform: "scale(1.01)",
  },
});

export const PrimaryButton = styled(Button)(({ theme }) => ({
  borderRadius: "12px",
  padding: "12px 24px",
  fontWeight: 600,
  textTransform: "none",
  fontSize: "16px",
  backgroundColor: PRIMARY_COLOR,
  color: "#fff",
  border: "none",
  boxShadow: `0 4px 16px ${PRIMARY_COLOR}30`,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  whiteSpace: "nowrap",
  minWidth: "auto",
  "&:hover": {
    backgroundColor: `${PRIMARY_COLOR}dd`,
    transform: "translateY(-2px)",
    boxShadow: `0 8px 24px ${PRIMARY_COLOR}40`,
  },
  "&:disabled": {
    opacity: 0.6,
    transform: "none",
    boxShadow: "none",
    backgroundColor: `${PRIMARY_COLOR}80`,
  },
  [theme.breakpoints.down("md")]: {
    padding: "10px 16px",
    fontSize: "14px",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "10px 16px",
    fontSize: "14px",
    width: "100%",
  },
}));

export const SecondaryButton = styled(Button)(({ theme }) => ({
  borderRadius: "12px",
  padding: "12px 24px",
  fontWeight: 600,
  textTransform: "none",
  fontSize: "16px",
  background: "#fff",
  color: NEUTRAL_800,
  border: `1px solid ${NEUTRAL_200}`,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  whiteSpace: "nowrap",
  minWidth: "auto",
  "&:hover": {
    borderColor: PRIMARY_COLOR,
    background: `${PRIMARY_COLOR}08`,
    transform: "translateY(-1px)",
  },
  [theme.breakpoints.down("md")]: {
    padding: "10px 16px",
    fontSize: "14px",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "10px 16px",
    fontSize: "14px",
    width: "100%",
  },
}));

export const UploadButton = styled(Button)<{ component?: React.ElementType }>(
  () => ({
    backgroundColor: PRIMARY_COLOR,
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    padding: "8px 24px",
    color: "#fff",
    "&:hover": {
      backgroundColor: `${PRIMARY_COLOR}dd`,
    },
    "&:disabled": {
      backgroundColor: `${PRIMARY_COLOR}80`,
    },
  })
);

export const ActionBar = styled(Box)(({ theme }) => ({
  position: "sticky",
  bottom: "20px",
  zIndex: 100,
  padding: "20px",
  background: "rgba(255, 255, 255, 0.95)",
  backdropFilter: "blur(20px)",
  borderRadius: "16px",
  border: `1px solid ${NEUTRAL_100}`,
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
  width: "100%",
  [theme.breakpoints.down("md")]: {
    position: "static",
    padding: "16px",
    borderRadius: "12px",
  },
  [theme.breakpoints.down("sm")]: {
    position: "static",
    padding: "12px",
    borderRadius: "12px",
    bottom: 0,
  },
}));

export const LoadingOverlay = styled(Box)({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: "rgba(255, 255, 255, 0.9)",
  backdropFilter: "blur(8px)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "16px",
  zIndex: 10,
});

export const FormGrid = styled(Box)({
  display: "grid",
  gap: "16px",
  "@media (min-width: 768px)": {
    gridTemplateColumns: "1fr 1fr",
  },
});

export const StatusChip = styled(Chip)({
  borderRadius: "8px",
  fontWeight: 600,
  fontSize: "12px",
  height: "28px",
});

export const ImagePreview = styled("img")({
  maxWidth: "100%",
  maxHeight: "200px",
  objectFit: "contain",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "scale(1.05)",
    boxShadow: "0 8px 30px rgba(0, 0, 0, 0.2)",
  },
});

export const AutoCompleteWrapper = styled(Box)({
  width: "100%",
  position: "relative",
  zIndex: 2000,
  "& [data-testid*='-optionsList']": {
    zIndex: 2100,
    position: "absolute",
    maxWidth: "none !important",
    width: "100% !important",
    minWidth: "300px",
    boxShadow: "0 12px 40px rgba(0, 0, 0, 0.2)",
    border: "1px solid rgba(245, 130, 32, 0.1)",
    borderRadius: "8px",
  },

  "&:focus-within": {
    zIndex: 2050,
    "& [data-testid*='-optionsList']": {
      zIndex: 2150,
    },
  },
});

export const ElevatedSectionCard = styled(SectionCard)({
  "&:has([data-testid*='-optionsList'])": {
    zIndex: 1500,
  },
  "&:focus-within": {
    zIndex: 1500,
  },
});
